import React, { useState, useEffect } from 'react';
import reviewService from '../services/reviewService';
import useDatabase from '../hooks/useDatabase';
import { useAuth } from '../hooks/useAuth';
import LoginModal from '../components/LoginModal';
import useUnits from '../hooks/useUnits';

const Avaliacoes = () => {
  const [userRating, setUserRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewForm, setReviewForm] = useState({
    name: '',
    turno: '',
    comment: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  // Authentication hook
  const { currentUser } = useAuth();
  
  // Database connection hook
  const { isConnected } = useDatabase();
  const { units, error: unitsError } = useUnits();

  useEffect(() => {
    // Prefill selected unit from logged user when available
    const unitId = currentUser?.unit?._id || currentUser?.unitId || currentUser?.unit || '';
    setSelectedUnitId(unitId || '');
  }, [currentUser]);

  const handleStarClick = (rating) => {
    setUserRating(rating);
  };

  const handleStarHover = (rating) => {
    setHoverRating(rating);
  };

  const handleStarLeave = () => {
    setHoverRating(0);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReviewForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user is authenticated
    if (!currentUser) {
      setIsLoginModalOpen(true);
      setSubmitStatus('error');
      setErrorMessage('Você precisa estar logado para enviar uma avaliação.');
      return;
    }
    
    if (userRating === 0) {
      setSubmitStatus('error');
      setErrorMessage('Por favor, selecione sua avaliação (estrelas).');
      return;
    }
    
    if (!reviewForm.name || !reviewForm.turno || !reviewForm.comment) {
      setSubmitStatus('error');
      setErrorMessage('Por favor, preencha Nome, Turno e Comentário.');
      return;
    }
    
    if (!isConnected) {
      setSubmitStatus('error');
      setErrorMessage('Sistema offline no momento. Tente novamente mais tarde.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const unitId = currentUser?.unit?._id || currentUser?.unitId || currentUser?.unit || selectedUnitId;
      if (!unitId) {
        setSubmitStatus('error');
        setErrorMessage('Selecione uma unidade para enviar sua avaliação.');
        setIsSubmitting(false);
        return;
      }
      const reviewData = {
        name: reviewForm.name,
        turno: reviewForm.turno,
        comment: reviewForm.comment,
        rating: userRating,
        email: currentUser.email, // Use logged-in user's email
        unit: unitId // Include user's unit (supports object or id)
      };
      
      const result = await reviewService.submitReview(reviewData);
      
      if (result.success) {
        setSubmitStatus('success');
        
        // Reset form on success
        setReviewForm({ name: '', turno: '', comment: '' });
        setUserRating(5);
        
        // Reviews would be reloaded here in a full implementation
      } else {
        setSubmitStatus('error');
        setErrorMessage(result.message || 'Erro ao enviar avaliação.');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage(error.message || 'Erro ao enviar avaliação.');
    } finally {
      setIsSubmitting(false);
    }
  };



  const ratingLabels = {
    1: { label: 'Muito Insatisfeito' },
    2: { label: 'Insatisfeito' },
    3: { label: 'Satisfeito' },
    4: { label: 'Muito Satisfeito' },
    5: { label: 'Excelente!' }
  };

  const renderStars = (rating, interactive = false, size = 'text-xl') => {
    const currentRating = interactive ? (hoverRating || userRating) : rating;
    
    return (
      <div className="flex flex-col items-center gap-2">
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map(star => (
            <button
              key={star}
              type={interactive ? 'button' : undefined}
              onClick={interactive ? () => handleStarClick(star) : undefined}
              onMouseEnter={interactive ? () => handleStarHover(star) : undefined}
              onMouseLeave={interactive ? handleStarLeave : undefined}
              className={`${size} ${interactive ? 'cursor-pointer hover:scale-110 transition-transform p-1' : 'cursor-default'} ${
                star <= currentRating
                  ? 'text-yellow-400 drop-shadow-sm'
                  : 'text-slate-300 dark:text-slate-600'
              }`}
              disabled={!interactive}
            >
              <i className={`ph-fill ${star <= currentRating ? 'ph-star' : 'ph-star'}`}></i>
            </button>
          ))}
        </div>
        
        {interactive && (
          <div className="h-8 flex items-center justify-center transition-all duration-300 ease-in-out">
            {currentRating > 0 ? (
              <span className="text-lg font-medium text-slate-600 dark:text-slate-300">
                {ratingLabels[currentRating]?.label}
              </span>
            ) : (
              <span className="text-sm text-slate-400 dark:text-slate-500">
                Toque nas estrelas para avaliar
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-8">
      

      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl md:text-5xl font-bold text-slate-800 dark:text-white mb-4">
          Avaliações
        </h1>
        <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
          Compartilhe sua experiência conosco
        </p>
      </div>

      {currentUser && (
        <div className="max-w-2xl mx-auto">
          {/* Review Form */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-6">
                Deixe sua Avaliação
              </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
              {/* Unidade (somente para admin ou quando não houver unidade no perfil) */}
              {(!currentUser?.unit || currentUser?.isAdmin) && (
                <div>
                  <label htmlFor="reviewUnit" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Unidade *
                  </label>
                  <select
                    id="reviewUnit"
                    name="unit"
                    value={selectedUnitId}
                    onChange={(e) => setSelectedUnitId(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  >
                    <option value="">Selecione a unidade</option>
                    {units.map((u) => (
                      <option key={u._id} value={u._id}>{u.displayName || u.name}</option>
                    ))}
                  </select>
                  {unitsError && (
                    <p className="text-sm text-red-600 dark:text-red-400 mt-2">{unitsError}</p>
                  )}
                </div>
              )}
              {/* Nome */}
              <div>
                <label htmlFor="reviewName" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Nome *
                </label>
                <input
                  type="text"
                  id="reviewName"
                  name="name"
                  value={reviewForm.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  placeholder="Digite seu nome"
                />
              </div>

              {/* Turno */}
              <div>
                <label htmlFor="reviewTurno" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Turno *
                </label>
                <select
                  id="reviewTurno"
                  name="turno"
                  value={reviewForm.turno}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                >
                  <option value="">Selecione o turno</option>
                  <option value="Matutino">Matutino</option>
                  <option value="Vespertino">Vespertino</option>
                  <option value="Diurno">Diurno</option>
                  <option value="Noturno">Noturno</option>
                </select>
              </div>

              {/* Rating Stars */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                  Sua Avaliação *
                </label>
                {renderStars(userRating, true, 'text-3xl')}
              </div>
              
              <div>
                <label htmlFor="reviewComment" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Comentário *
                </label>
                <textarea
                  id="reviewComment"
                  name="comment"
                  value={reviewForm.comment}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-white resize-none"
                  placeholder="Conte-nos sobre sua experiência..."
                ></textarea>
              </div>
              
              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded-lg">
                  <div className="flex items-center">
                    <i className="ph ph-check-circle text-lg mr-2"></i>
                    <span>Avaliação enviada com sucesso! Obrigado pelo seu feedback.</span>
                  </div>
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg">
                  <div className="flex items-center">
                    <i className="ph ph-warning-circle text-lg mr-2"></i>
                    <span>{errorMessage || 'Por favor, selecione uma avaliação e preencha todos os campos.'}</span>
                  </div>
                </div>
              )}
              
              <button
                type="submit"
                disabled={isSubmitting || !isConnected}
                className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>{isSubmitting ? 'Enviando...' : 'Enviar Avaliação'}</span>
                {isSubmitting ? (
                  <i className="ph ph-spinner-gap text-lg ml-2 animate-spin"></i>
                ) : (
                  <i className="ph ph-paper-plane-tilt text-lg ml-2"></i>
                )}
              </button>
            </form>
        </div>
      </div>
      )}
      
      {/* Modal de Login */}
      <LoginModal 
        isOpen={isLoginModalOpen} 
        onClose={() => setIsLoginModalOpen(false)} 
      />
    </div>
  );
};

export default Avaliacoes;